/**
 * ComplianceDrawer Component
 * Create / Edit Compliance Item - large drawer (multi-section, type-aware fields)
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronUp, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  FormSwitch,
  Input,
  NumberInput,
  PhoneInput,
  TextArea,
} from '../forms';
import type {
  ComplianceCondition,
  ComplianceItem,
  ComplianceItemStatus,
  ComplianceItemType,
  ConditionStatus,
  CreateComplianceItemRequest,
  InsuranceType,
  InspectionResult,
  PermitType,
} from '../../types/compliance';
import {
  useCreateComplianceItemMutation,
  useUpdateComplianceItemMutation,
} from '../../services/complianceApi';
import { useToast } from '../../contexts/ToastContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const ITEM_TYPE_OPTIONS = [
  { value: 'permit', label: 'Permit' },
  { value: 'insurance', label: 'Insurance Certificate' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'certification', label: 'Certification' },
  { value: 'requirement', label: 'Requirement' },
];

const STATUS_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'applied', label: 'Applied' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'issued', label: 'Issued' },
  { value: 'expired', label: 'Expired' },
  { value: 'closed', label: 'Closed' },
  { value: 'valid', label: 'Valid' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
];

const PERMIT_TYPE_OPTIONS = [
  { value: 'building', label: 'Building' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'fire', label: 'Fire' },
  { value: 'other', label: 'Other' },
];

const INSURANCE_TYPE_OPTIONS = [
  { value: 'general_liability', label: 'General Liability' },
  { value: 'auto', label: 'Auto' },
  { value: 'workers_comp', label: "Workers' Comp" },
  { value: 'umbrella', label: 'Umbrella' },
  { value: 'professional', label: 'Professional' },
];

const INSPECTION_RESULT_OPTIONS = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'partial', label: 'Partial' },
];

const CONDITION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'met', label: 'Met' },
  { value: 'waived', label: 'Waived' },
];

// ============================================================================
// HELPERS
// ============================================================================

const formatDateForInput = (dateStr?: string): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
};

const emptyCondition = (): ComplianceCondition => ({
  description: '',
  status: 'pending',
});

// ============================================================================
// PROPS
// ============================================================================

interface ComplianceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  item?: ComplianceItem | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

const ComplianceDrawer: React.FC<ComplianceDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  item,
}) => {
  const toast = useToast();
  const isEditing = !!item;

  const [createItem, { isLoading: isCreating }] = useCreateComplianceItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateComplianceItemMutation();
  const isSaving = isCreating || isUpdating;

  // ---- Core form state ----
  const [itemType, setItemType] = useState<ComplianceItemType>(item?.itemType || 'permit');
  const [name, setName] = useState<string>(item?.name || '');
  const [description, setDescription] = useState<string>(item?.description || '');
  const [status, setStatus] = useState<ComplianceItemStatus>(item?.status || 'not_applied');
  const [notes, setNotes] = useState<string>(item?.notes || '');

  // ---- Permit fields ----
  const [permitNumber, setPermitNumber] = useState<string>(item?.permitNumber || '');
  const [permitType, setPermitType] = useState<PermitType | ''>(item?.permitType || '');
  const [issuingAuthority, setIssuingAuthority] = useState<string>(
    item?.issuingAuthority || ''
  );
  const [jurisdiction, setJurisdiction] = useState<string>(item?.jurisdiction || '');

  // ---- Insurance fields ----
  const [certificateNumber, setCertificateNumber] = useState<string>(
    item?.certificateNumber || ''
  );
  const [insuranceType, setInsuranceType] = useState<InsuranceType | ''>(
    item?.insuranceType || ''
  );
  const [insurer, setInsurer] = useState<string>(item?.insurer || '');
  const [policyNumber, setPolicyNumber] = useState<string>(item?.policyNumber || '');
  const [coverageAmount, setCoverageAmount] = useState<number | undefined>(item?.coverageAmount);

  // ---- Inspection fields ----
  const [inspectionType, setInspectionType] = useState<string>(item?.inspectionType || '');
  const [scheduledDate, setScheduledDate] = useState<string>(
    formatDateForInput(item?.scheduledDate)
  );
  const [inspectorName, setInspectorName] = useState<string>(item?.inspectorName || '');
  const [inspectorPhone, setInspectorPhone] = useState<string>(item?.inspectorPhone || '');
  const [result, setResult] = useState<InspectionResult | ''>(item?.result || '');
  const [inspectedDate, setInspectedDate] = useState<string>(
    formatDateForInput(item?.inspectedDate)
  );

  // ---- Shared date fields ----
  const [applicationDate, setApplicationDate] = useState<string>(
    formatDateForInput(item?.applicationDate)
  );
  const [issuedDate, setIssuedDate] = useState<string>(formatDateForInput(item?.issuedDate));
  const [expirationDate, setExpirationDate] = useState<string>(
    formatDateForInput(item?.expirationDate)
  );

  // ---- Fees ----
  const [applicationFee, setApplicationFee] = useState<number | undefined>(
    item?.fees?.applicationFee
  );
  const [permitFee, setPermitFee] = useState<number | undefined>(item?.fees?.permitFee);
  const [otherFees, setOtherFees] = useState<number | undefined>(item?.fees?.otherFees);
  const [feePaid, setFeePaid] = useState<boolean>(item?.fees?.feePaid ?? false);

  // ---- Conditions ----
  const [conditions, setConditions] = useState<ComplianceCondition[]>(
    item?.conditions?.length ? item.conditions : []
  );

  // ---- Section visibility ----
  const [showDates, setShowDates] = useState(false);
  const [showFees, setShowFees] = useState(false);
  const [showConditions, setShowConditions] = useState(false);

  // ---- Errors ----
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset on open / item change
  useEffect(() => {
    if (isOpen) {
      setItemType(item?.itemType || 'permit');
      setName(item?.name || '');
      setDescription(item?.description || '');
      setStatus(item?.status || 'not_applied');
      setNotes(item?.notes || '');
      setPermitNumber(item?.permitNumber || '');
      setPermitType(item?.permitType || '');
      setIssuingAuthority(item?.issuingAuthority || '');
      setJurisdiction(item?.jurisdiction || '');
      setCertificateNumber(item?.certificateNumber || '');
      setInsuranceType(item?.insuranceType || '');
      setInsurer(item?.insurer || '');
      setPolicyNumber(item?.policyNumber || '');
      setCoverageAmount(item?.coverageAmount);
      setInspectionType(item?.inspectionType || '');
      setScheduledDate(formatDateForInput(item?.scheduledDate));
      setInspectorName(item?.inspectorName || '');
      setInspectorPhone(item?.inspectorPhone || '');
      setResult(item?.result || '');
      setInspectedDate(formatDateForInput(item?.inspectedDate));
      setApplicationDate(formatDateForInput(item?.applicationDate));
      setIssuedDate(formatDateForInput(item?.issuedDate));
      setExpirationDate(formatDateForInput(item?.expirationDate));
      setApplicationFee(item?.fees?.applicationFee);
      setPermitFee(item?.fees?.permitFee);
      setOtherFees(item?.fees?.otherFees);
      setFeePaid(item?.fees?.feePaid ?? false);
      setConditions(item?.conditions?.length ? item.conditions : []);
      setErrors({});
      setShowDates(false);
      setShowFees(false);
      setShowConditions(false);
    }
  }, [isOpen, item]);

  // --------------------------------------------------------------------------
  // VALIDATION
  // --------------------------------------------------------------------------

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!itemType) newErrors.itemType = 'Item type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --------------------------------------------------------------------------
  // SUBMIT
  // --------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload: CreateComplianceItemRequest = {
      itemType,
      name: name.trim(),
      description: description.trim() || undefined,
      status,
      notes: notes.trim() || undefined,
    };

    // Permit-specific
    if (itemType === 'permit') {
      if (permitNumber) payload.permitNumber = permitNumber.trim();
      if (permitType) payload.permitType = permitType as PermitType;
      if (issuingAuthority) payload.issuingAuthority = issuingAuthority.trim();
      if (jurisdiction) payload.jurisdiction = jurisdiction.trim();
    }

    // Insurance-specific
    if (itemType === 'insurance') {
      if (certificateNumber) payload.certificateNumber = certificateNumber.trim();
      if (insuranceType) payload.insuranceType = insuranceType as InsuranceType;
      if (insurer) payload.insurer = insurer.trim();
      if (policyNumber) payload.policyNumber = policyNumber.trim();
      if (coverageAmount !== undefined) payload.coverageAmount = coverageAmount;
    }

    // Inspection-specific
    if (itemType === 'inspection') {
      if (inspectionType) payload.inspectionType = inspectionType.trim();
      if (scheduledDate) payload.scheduledDate = scheduledDate;
      if (inspectorName) payload.inspectorName = inspectorName.trim();
      if (inspectorPhone) payload.inspectorPhone = inspectorPhone.trim();
      if (result) payload.result = result as InspectionResult;
      if (inspectedDate) payload.inspectedDate = inspectedDate;
    }

    // Shared dates
    if (applicationDate) payload.applicationDate = applicationDate;
    if (issuedDate) payload.issuedDate = issuedDate;
    if (expirationDate) payload.expirationDate = expirationDate;

    // Fees
    if (
      applicationFee !== undefined ||
      permitFee !== undefined ||
      otherFees !== undefined ||
      feePaid
    ) {
      payload.fees = { feePaid };
      if (applicationFee !== undefined) payload.fees.applicationFee = applicationFee;
      if (permitFee !== undefined) payload.fees.permitFee = permitFee;
      if (otherFees !== undefined) payload.fees.otherFees = otherFees;
    }

    // Conditions
    const validConditions = conditions.filter(c => c.description.trim());
    if (validConditions.length > 0) payload.conditions = validConditions;

    try {
      if (isEditing && item) {
        await updateItem({ projectId, itemId: item._id, data: payload }).unwrap();
        toast.success('Compliance item updated successfully');
      } else {
        await createItem({ projectId, data: payload }).unwrap();
        toast.success('Compliance item created successfully');
      }
      onClose();
    } catch {
      toast.error(
        isEditing ? 'Failed to update compliance item' : 'Failed to create compliance item'
      );
    }
  };

  // --------------------------------------------------------------------------
  // CONDITIONS HANDLERS
  // --------------------------------------------------------------------------

  const addCondition = () => setConditions(prev => [...prev, emptyCondition()]);
  const removeCondition = (idx: number) =>
    setConditions(prev => prev.filter((_, i) => i !== idx));
  const updateConditionField = (
    idx: number,
    key: keyof ComplianceCondition,
    val: string
  ) => {
    setConditions(prev => prev.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  if (!isOpen) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - large (multiple sections) */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right duration-200">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Compliance Item' : 'Add Compliance Item'}
              </h2>
              {isEditing && item && (
                <p className="text-sm text-gray-500 mt-0.5">{item.itemNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* ---- BASIC INFORMATION ---- */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    options={ITEM_TYPE_OPTIONS}
                    value={itemType}
                    onChange={(val) => setItemType(val as ComplianceItemType)}
                    placeholder="Select type"
                  />
                  {errors.itemType && (
                    <p className="text-xs text-red-500 mt-1">{errors.itemType}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Status
                  </label>
                  <Dropdown
                    options={STATUS_OPTIONS}
                    value={status}
                    onChange={(val) => setStatus(val as ComplianceItemStatus)}
                    placeholder="Select status"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Compliance item name"
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Description
                  </label>
                  <TextArea
                    value={description}
                    onChange={setDescription}
                    placeholder="Describe this compliance item"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* ---- PERMIT FIELDS ---- */}
            {itemType === 'permit' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Permit Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Permit Number
                    </label>
                    <Input
                      value={permitNumber}
                      onChange={e => setPermitNumber(e.target.value)}
                      placeholder="e.g. BLDG-2024-001"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Permit Type
                    </label>
                    <Dropdown
                      options={PERMIT_TYPE_OPTIONS}
                      value={permitType}
                      onChange={(val) => setPermitType(val as PermitType)}
                      placeholder="Select permit type"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Issuing Authority
                    </label>
                    <Input
                      value={issuingAuthority}
                      onChange={e => setIssuingAuthority(e.target.value)}
                      placeholder="e.g. City Planning Department"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Jurisdiction
                    </label>
                    <Input
                      value={jurisdiction}
                      onChange={e => setJurisdiction(e.target.value)}
                      placeholder="e.g. Los Angeles County"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ---- INSURANCE FIELDS ---- */}
            {itemType === 'insurance' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Insurance Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Certificate Number
                    </label>
                    <Input
                      value={certificateNumber}
                      onChange={e => setCertificateNumber(e.target.value)}
                      placeholder="Certificate number"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Insurance Type
                    </label>
                    <Dropdown
                      options={INSURANCE_TYPE_OPTIONS}
                      value={insuranceType}
                      onChange={(val) => setInsuranceType(val as InsuranceType)}
                      placeholder="Select insurance type"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Insurer / Insurance Company
                    </label>
                    <Input
                      value={insurer}
                      onChange={e => setInsurer(e.target.value)}
                      placeholder="Insurance company name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Policy Number
                    </label>
                    <Input
                      value={policyNumber}
                      onChange={e => setPolicyNumber(e.target.value)}
                      placeholder="Policy number"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Coverage Amount
                    </label>
                    <CurrencyInput
                      value={coverageAmount}
                      onChange={setCoverageAmount}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ---- INSPECTION FIELDS ---- */}
            {itemType === 'inspection' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Inspection Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Inspection Type
                    </label>
                    <Input
                      value={inspectionType}
                      onChange={e => setInspectionType(e.target.value)}
                      placeholder="e.g. Structural Framing, Electrical Rough-In"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Inspector Name
                    </label>
                    <Input
                      value={inspectorName}
                      onChange={e => setInspectorName(e.target.value)}
                      placeholder="Inspector name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Inspector Phone
                    </label>
                    <PhoneInput
                      value={inspectorPhone}
                      onChange={value => setInspectorPhone(value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Scheduled Date
                    </label>
                    <DatePicker
                      value={scheduledDate}
                      onChange={setScheduledDate}
                      placeholder="Select date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Inspected Date
                    </label>
                    <DatePicker
                      value={inspectedDate}
                      onChange={setInspectedDate}
                      placeholder="Select date"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Result
                    </label>
                    <Dropdown
                      options={INSPECTION_RESULT_OPTIONS}
                      value={result}
                      onChange={(val) => setResult(val as InspectionResult)}
                      placeholder="Select result"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ---- DATES (collapsible) ---- */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowDates(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Dates</span>
                {showDates ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>
              {showDates && (
                <div className="px-4 py-4 grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Application Date
                    </label>
                    <DatePicker
                      value={applicationDate}
                      onChange={setApplicationDate}
                      placeholder="Select date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Issued Date
                    </label>
                    <DatePicker
                      value={issuedDate}
                      onChange={setIssuedDate}
                      placeholder="Select date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Expiration Date
                    </label>
                    <DatePicker
                      value={expirationDate}
                      onChange={setExpirationDate}
                      placeholder="Select date"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ---- FEES (collapsible) ---- */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowFees(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Fees</span>
                {showFees ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>
              {showFees && (
                <div className="px-4 py-4 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Application Fee
                      </label>
                      <CurrencyInput
                        value={applicationFee}
                        onChange={setApplicationFee}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Permit Fee
                      </label>
                      <CurrencyInput
                        value={permitFee}
                        onChange={setPermitFee}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Other Fees
                      </label>
                      <CurrencyInput
                        value={otherFees}
                        onChange={setOtherFees}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FormSwitch
                      checked={feePaid}
                      onChange={(checked: boolean) => setFeePaid(checked)}
                    />
                    <span className="text-sm text-gray-700">Fee Paid</span>
                  </div>
                </div>
              )}
            </div>

            {/* ---- CONDITIONS (collapsible) ---- */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowConditions(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">
                  Conditions
                  {conditions.length > 0 && (
                    <span className="ml-2 text-xs text-gray-500">({conditions.length})</span>
                  )}
                </span>
                {showConditions ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>
              {showConditions && (
                <div className="px-4 py-4 space-y-3">
                  {conditions.map((cond, idx) => (
                    <div key={idx} className="border border-gray-100 rounded-lg p-3 space-y-3">
                      <div className="flex items-start justify-between">
                        <span className="text-xs font-medium text-gray-600">
                          Condition {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeCondition(idx)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Description
                        </label>
                        <TextArea
                          value={cond.description}
                          onChange={(val) => updateConditionField(idx, 'description', val)}
                          placeholder="Describe this condition"
                          rows={2}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Status
                          </label>
                          <Dropdown
                            options={CONDITION_STATUS_OPTIONS}
                            value={cond.status}
                            onChange={(val) =>
                              updateConditionField(idx, 'status', val as ConditionStatus)
                            }
                            placeholder="Select status"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Due Date
                          </label>
                          <DatePicker
                            value={cond.dueDate ? formatDateForInput(cond.dueDate) : ''}
                            onChange={(val) => updateConditionField(idx, 'dueDate', val)}
                            placeholder="Select date"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addCondition}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
                  >
                    + Add Condition
                  </button>
                </div>
              )}
            </div>

            {/* ---- NOTES ---- */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <TextArea
                value={notes}
                onChange={setNotes}
                placeholder="Additional notes or context"
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 flex-shrink-0 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:bg-brand-200 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : isEditing ? 'Update Item' : 'Create Item'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default ComplianceDrawer;
